import { useCallback, useMemo } from "react";
import { Message, MessagesState } from "@/lib/core/crm/types";

export function useGetters(state: MessagesState) {
  // Get messages for a specific inbox/provider
  const getMessages = useCallback(
    (inboxId: string, provider: string, filter: string = "all") => {
      const messages = state.messagesByInbox[inboxId]?.[provider] || [];

      if (filter === "all") return messages;
      if (filter === "unread") return messages.filter((msg) => !msg.isRead);
      if (filter === "important") return messages.filter((msg) => msg.importance === "high");

      return messages;
    },
    [state.messagesByInbox]
  );

  // Get unread count for a specific inbox/provider
  const getUnreadCount = useCallback(
    (inboxId: string, provider: string) => {
      return state.unreadCounts[inboxId]?.[provider] || 0;
    },
    [state.unreadCounts]
  );

  // Check loading state
  const isLoading = useCallback(
    (inboxId: string, provider: string) => {
      return state.loadingStates[inboxId]?.[provider] || false;
    },
    [state.loadingStates]
  );

  // Check error state
  const hasError = useCallback(
    (inboxId: string, provider: string) => {
      return !!state.errorStates[inboxId]?.[provider];
    },
    [state.errorStates]
  );

  // Get error message
  const getError = useCallback(
    (inboxId: string, provider: string) => {
      return state.errorStates[inboxId]?.[provider] || null;
    },
    [state.errorStates]
  );

  // Check if has more messages
  const hasMore = useCallback(
    (inboxId: string, provider: string) => {
      return state.paginationInfo[inboxId]?.[provider]?.hasMore || false;
    },
    [state.paginationInfo]
  );

  // Stable slice object: only re-created when one of its (already memoized)
  // members changes. A fresh object literal here would defeat the provider's
  // useMemo — see docs/engineering-rules/MEMO_STABLE_PROPS.md.
  return useMemo(
    () => ({
      getMessages,
      getUnreadCount,
      isLoading,
      hasError,
      getError,
      hasMore,
    }),
    [getMessages, getUnreadCount, isLoading, hasError, getError, hasMore]
  );
}
